import { Link } from "react-router-dom";

const steps = [
  {
    num: "01",
    icon: "ri-user-received-2-line",
    title: "You Invest",
    desc: "Commit capital from £10,000 into the Thorpe Dellow SPV. Full documentation and investor eligibility confirmed before funds move.",
  },
  {
    num: "02",
    icon: "ri-building-2-line",
    title: "SPV Acquires Properties",
    desc: "Your capital is pooled inside a ring-fenced Special Purpose Vehicle that acquires UK assisted living properties. First legal charges secured.",
  },
  {
    num: "03",
    icon: "ri-file-text-line",
    title: "25-Year Housing Association Lease",
    desc: "Each property is leased to a registered UK housing association under a Full Repair & Insurance agreement with CPI+1% annual rent reviews.",
  },
  {
    num: "04",
    icon: "ri-money-pound-circle-line",
    title: "Monthly Income to You",
    desc: "Rental income flows back through the SPV and is distributed to you every month — targeting 10.00% per annum for the full 5-year term.",
  },
];

export default function SocialHousingOverview() {
  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Top: image + intro in 2-col */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-16">

          {/* Image side */}
          <div className="relative">
            <div className="w-full rounded-3xl overflow-hidden" style={{ height: 420 }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20aerial%20view%20of%20a%20modern%20UK%20assisted%20living%20social%20housing%20estate%2C%20rows%20of%20well%20maintained%20red%20brick%20residential%20properties%2C%20neat%20gardens%20and%20quiet%20suburban%20streets%2C%20Yorkshire%20England%2C%20warm%20golden%20late%20afternoon%20light%2C%20investment%20grade%20residential%20property%20portfolio%2C%20no%20people%2C%20wide%20angle%20architectural%20real%20estate%20photography&width=700&height=420&seq=805&orientation=landscape"
                alt="UK social housing investment assets"
                className="w-full h-full object-cover object-top"
              />
              {/* Gradient overlay on bottom of image */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#071A2E]/40 via-transparent to-transparent rounded-3xl" />
            </div>

            {/* Floating income badge */}
            <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 bg-[#071A2E] rounded-2xl px-4 py-4 text-white">
              <p className="text-xs text-slate-400 mb-0.5">Monthly Target Income</p>
              <p
                className="text-2xl md:text-3xl font-bold text-[#C9A84C]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                £833<span className="text-base font-semibold text-slate-300">/mo</span>
              </p>
              <p className="text-xs text-slate-400 mt-0.5">per £100,000 invested</p>
            </div>

            {/* First charge badge */}
            <div className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2">
              <div className="w-5 h-5 flex items-center justify-center text-[#C9A84C]">
                <i className="ri-shield-check-fill text-base leading-none" />
              </div>
              <span className="text-[#071A2E] text-xs font-bold">First Legal Charge Security</span>
            </div>

            {/* Second image — property interior */}
            <div className="mt-4 w-full rounded-2xl overflow-hidden hidden md:block" style={{ height: 180 }}>
              <img
                src="https://readdy.ai/api/search-image?query=interior%20of%20a%20modern%20UK%20assisted%20living%20supported%20housing%20lounge%2C%20clean%20well%20furnished%20communal%20area%2C%20professional%20residential%20care%20environment%2C%20bright%20natural%20light%20through%20windows%2C%20investment%20grade%20interior%20real%20estate%20photography%2C%20no%20people%2C%20warm%20welcoming%20atmosphere&width=700&height=180&seq=806&orientation=landscape"
                alt="UK assisted living interior"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          {/* Content side */}
          <div className="flex flex-col justify-center">
            <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest mb-4 block">
              How It Works
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#071A2E] leading-tight mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              A Structured Path to
              <br />
              <span className="text-[#C9A84C]">Monthly Income</span>
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              The Thorpe Dellow Social Housing Structured Product deploys your capital through a carefully engineered four-step arrangement — from your initial investment all the way to consistent monthly income, secured at every stage.
            </p>

            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-3 border-t border-slate-100 pt-6 mb-8">
              {[
                { value: "10.00%", label: "Target p.a." },
                { value: "25yr", label: "Max Lease" },
                { value: "FRI", label: "Lease Type" },
              ].map((s) => (
                <div key={s.label} className="text-center bg-[#EEF2F9] rounded-xl py-4">
                  <p
                    className="text-xl font-bold text-[#C9A84C]"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            <Link
              to="/social-housing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#071A2E] text-white font-semibold text-sm hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap w-fit"
            >
              View Full Product Details
              <i className="ri-arrow-right-line leading-none" />
            </Link>
          </div>
        </div>

        {/* Bottom: Visual 4-step process flow */}
        <div className="relative">
          {/* Section label */}
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-[#EEF2F9] border border-slate-200 text-[#071A2E] text-[10px] font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full">
              <i className="ri-route-line text-[#C9A84C] text-xs leading-none" />
              The Investment Journey — 4 Steps
            </span>
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-[2px] z-0"
              style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", opacity: 0.3 }}
            />

            {steps.map((step, i) => (
              <div key={step.num} className="relative z-10 flex flex-col items-center text-center px-4 py-6 group">
                {/* Arrow between cards on desktop */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-3 top-10 z-20 w-6 h-6 items-center justify-center">
                    <i className="ri-arrow-right-line text-[#C9A84C] text-lg leading-none" />
                  </div>
                )}
                {/* Arrow between cards on mobile (vertical) */}
                {i < steps.length - 1 && (
                  <div className="lg:hidden flex items-center justify-center mt-2 mb-0">
                    <i className="ri-arrow-down-line text-[#C9A84C]/40 text-xl leading-none" />
                  </div>
                )}

                {/* Icon circle */}
                <div
                  className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-4 relative transition-all duration-300 group-hover:scale-105"
                  style={{
                    background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 100%)",
                    border: "1.5px solid rgba(201,168,76,0.3)",
                  }}
                >
                  <i className={`${step.icon} text-[#C9A84C] text-2xl md:text-3xl leading-none`} />
                  {/* Step number badge */}
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold text-[#071A2E]"
                    style={{ background: "linear-gradient(135deg, #C9A84C, #E2C27A)" }}
                  >
                    {step.num}
                  </span>
                </div>

                <h3
                  className="font-bold text-[#071A2E] text-sm mb-2 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.title}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-8 flex flex-wrap justify-center gap-3 md:gap-6">
            {[
              { icon: "ri-shield-check-line", text: "First Legal Charge on All Properties" },
              { icon: "ri-user-star-line", text: "Independent Security Trustee" },
              { icon: "ri-line-chart-line", text: "CPI + 1% Annual Rent Increases" },
              { icon: "ri-calendar-check-line", text: "No Void Periods Since 2006" },
            ].map((t) => (
              <div key={t.text} className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${t.icon} text-[#C9A84C] leading-none`} />
                </div>
                <span className="whitespace-nowrap">{t.text}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
